import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  HStack,
  Input,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ADMIN_ENDPOINTS } from '../../../api/endpoints';

const formatDate = (value) => value ? new Date(value).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const personName = (person) => [person?.first_name, person?.last_name].filter(Boolean).join(' ') || person?.email || 'Unknown user';

export default function TripPlannerAdmin() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [analytics, setAnalytics] = useState(null);
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filters, setFilters] = useState({ creator: '', destination: '', status: '', shared: '' });
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const rowHover = useColorModeValue('gray.50', 'gray.700');
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` }), []);

  const loadAnalytics = useCallback(async () => {
    const response = await axios.get(ADMIN_ENDPOINTS.tripPlanner.analytics, { headers });
    setAnalytics(response.data?.data?.analytics || null);
  }, [headers]);

  const loadPlans = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(ADMIN_ENDPOINTS.tripPlanner.plans, { headers, params: { page, limit: 20, ...filters } });
      setPlans(response.data?.data?.plans || []);
      setPagination(response.data?.data?.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (error) {
      toast({ title: 'Unable to load planner data', description: error?.response?.data?.message || error.message, status: 'error', isClosable: true });
    } finally { setLoading(false); }
  }, [filters, headers, toast]);

  useEffect(() => { void loadAnalytics(); void loadPlans(1); }, [loadAnalytics, loadPlans]);

  const openPlan = async (planId) => {
    setDetailLoading(true);
    setSelectedPlan(null);
    onOpen();
    try {
      const response = await axios.get(ADMIN_ENDPOINTS.tripPlanner.plan(planId), { headers });
      setSelectedPlan(response.data?.data?.plan || null);
    } catch (error) {
      toast({ title: 'Unable to load this plan', description: error?.response?.data?.message || error.message, status: 'error' });
      onClose();
    } finally { setDetailLoading(false); }
  };

  const disableShare = async () => {
    if (!selectedPlan?._id) return;
    setDisabling(true);
    try {
      await axios.delete(ADMIN_ENDPOINTS.tripPlanner.disableShare(selectedPlan._id), { headers });
      setSelectedPlan((current) => ({ ...current, sharing: { ...current.sharing, enabled: false } }));
      setPlans((current) => current.map((plan) => plan._id === selectedPlan._id ? { ...plan, sharing: { ...plan.sharing, enabled: false } } : plan));
      toast({ title: 'Trip sharing disabled', status: 'success' });
      void loadAnalytics();
    } catch (error) {
      toast({ title: 'Unable to disable sharing', description: error?.response?.data?.message || error.message, status: 'error' });
    } finally { setDisabling(false); }
  };

  const cards = analytics ? [
    ['Total plans', analytics.totalPlans], ['Saved plans', analytics.saved], ['Draft plans', analytics.drafts],
    ['Actively shared', analytics.sharedPlans], ['Unique viewers', analytics.uniqueViewers], ['Total views', analytics.totalViews],
    ['Created today', analytics.today], ['This month', analytics.thisMonth],
  ] : [];

  return <Box p={{ base: 4, md: 8 }}>
    <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3} mb={6}>
      <Box><Text fontSize="2xl" fontWeight="800">AI Planner</Text><Text color={muted}>See who creates plans, how they are shared and who views them.</Text></Box>
      <Badge px={3} py={2} borderRadius="full" colorScheme="yellow">{pagination.total || 0} plans</Badge>
    </Flex>

    <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4} mb={6}>
      {cards.map(([label, value]) => <Stat key={label} bg={cardBg} border="1px" borderColor={borderColor} borderRadius="xl" p={4}><StatLabel color={muted}>{label}</StatLabel><StatNumber>{Number(value || 0).toLocaleString()}</StatNumber></Stat>)}
    </Grid>

    {analytics?.destinations?.length > 0 && <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="xl" p={4} mb={6}><Text fontWeight="700" mb={3}>Popular destinations</Text><HStack spacing={2} flexWrap="wrap">{analytics.destinations.map((item) => <Badge key={item.destination} colorScheme="yellow" px={3} py={1.5} borderRadius="full">{item.destination} · {item.count}</Badge>)}</HStack></Box>}

    <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="xl" overflow="hidden">
      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 160px 160px auto' }} gap={3} p={4} borderBottom="1px" borderColor={borderColor}>
        <Input placeholder="Creator name or email" value={filters.creator} onChange={(event) => setFilters((current) => ({ ...current, creator: event.target.value }))} />
        <Input placeholder="Destination" value={filters.destination} onChange={(event) => setFilters((current) => ({ ...current, destination: event.target.value }))} />
        <Select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}><option value="">All statuses</option><option value="saved">Saved</option><option value="draft">Draft</option></Select>
        <Select value={filters.shared} onChange={(event) => setFilters((current) => ({ ...current, shared: event.target.value }))}><option value="">All sharing</option><option value="true">Shared</option><option value="false">Not shared</option></Select>
        <Button colorScheme="yellow" onClick={() => void loadPlans(1)}>Apply</Button>
      </Grid>
      <Box overflowX="auto">
        <Table size="sm"><Thead><Tr><Th>Plan</Th><Th>Created by</Th><Th>Destination</Th><Th>Status</Th><Th>Sharing</Th><Th isNumeric>Viewers</Th><Th isNumeric>Views</Th><Th>Updated</Th></Tr></Thead><Tbody>
          {loading ? <Tr><Td colSpan={8} textAlign="center" py={10}><Spinner color="yellow.400" /></Td></Tr> : plans.length === 0 ? <Tr><Td colSpan={8} textAlign="center" py={10} color={muted}>No plans match these filters.</Td></Tr> : plans.map((plan) => <Tr key={plan._id} cursor="pointer" _hover={{ bg: rowHover }} onClick={() => void openPlan(plan._id)}><Td fontWeight="700">{plan.title}</Td><Td><Text fontWeight="600">{personName(plan.userId)}</Text><Text fontSize="xs" color={muted}>{plan.userId?.email}</Text></Td><Td>{plan.intent?.specificArea || plan.intent?.destination || '—'}</Td><Td><Badge colorScheme={plan.status === 'saved' ? 'green' : 'gray'}>{plan.status}</Badge></Td><Td><Badge colorScheme={plan.sharing?.enabled ? 'purple' : 'gray'}>{plan.sharing?.enabled ? 'Active' : 'Off'}</Badge></Td><Td isNumeric>{plan.viewStats?.uniqueViewers || 0}</Td><Td isNumeric>{plan.viewStats?.totalViews || 0}</Td><Td>{formatDate(plan.updatedAt)}</Td></Tr>)}
        </Tbody></Table>
      </Box>
      <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor={borderColor}><Button size="sm" isDisabled={pagination.page <= 1} onClick={() => void loadPlans(pagination.page - 1)}>Previous</Button><Text fontSize="sm">Page {pagination.page} of {pagination.totalPages || 1}</Text><Button size="sm" isDisabled={pagination.page >= pagination.totalPages} onClick={() => void loadPlans(pagination.page + 1)}>Next</Button></Flex>
    </Box>

    <Drawer isOpen={isOpen} placement="right" size="xl" onClose={onClose}><DrawerOverlay /><DrawerContent><DrawerCloseButton /><DrawerHeader>Planner details</DrawerHeader><DrawerBody pb={8}>{detailLoading ? <Flex justify="center" py={16}><Spinner color="yellow.400" /></Flex> : selectedPlan && <VStack align="stretch" spacing={5}>
      <Box><Text fontSize="xl" fontWeight="800">{selectedPlan.title}</Text><Text color={muted}>{selectedPlan.intent?.specificArea || selectedPlan.intent?.destination || 'Flexible destination'} · {selectedPlan.intent?.guests || 'Flexible'} guests</Text></Box>
      <Grid templateColumns="repeat(3, 1fr)" gap={3}><Stat><StatLabel>Status</StatLabel><StatNumber fontSize="md">{selectedPlan.status}</StatNumber></Stat><Stat><StatLabel>Interested stays</StatLabel><StatNumber fontSize="md">{selectedPlan.stayPreferences?.interestedApartmentIds?.length || 0}</StatNumber></Stat><Stat><StatLabel>Activities</StatLabel><StatNumber fontSize="md">{selectedPlan.activities?.length || 0}</StatNumber></Stat></Grid>
      <Divider /><Box><Text fontWeight="700">Created by</Text><Text mt={1}>{personName(selectedPlan.userId)}</Text><Text color={muted}>{selectedPlan.userId?.email} · {selectedPlan.userId?.phone || 'No phone'}</Text></Box>
      <Box><Flex justify="space-between" align="center"><Box><Text fontWeight="700">Sharing</Text><Text color={muted} fontSize="sm">{selectedPlan.sharing?.enabled ? 'Members with the link can view' : 'Link disabled'}</Text></Box>{selectedPlan.sharing?.enabled && <Button colorScheme="red" variant="outline" size="sm" isLoading={disabling} onClick={() => void disableShare()}>Disable link</Button>}</Flex></Box>
      <Divider /><Box><Text fontWeight="700" mb={3}>Registered viewers ({selectedPlan.viewers?.length || 0})</Text>{selectedPlan.viewers?.length ? <VStack align="stretch" spacing={2}>{selectedPlan.viewers.map((view) => <Box key={view._id} border="1px" borderColor={borderColor} borderRadius="lg" p={3}><Flex justify="space-between" gap={3}><Box><Text fontWeight="700">{personName(view.viewerId)}</Text><Text fontSize="sm" color={muted}>{view.viewerId?.email}</Text></Box><Box textAlign="right"><Text fontWeight="700">{view.viewCount} view{view.viewCount === 1 ? '' : 's'}</Text><Text fontSize="xs" color={muted}>Last {formatDate(view.lastViewedAt)}</Text></Box></Flex></Box>)}</VStack> : <Text color={muted}>No one has viewed this shared plan yet.</Text>}</Box>
      {selectedPlan.itinerary && <><Divider /><Box><Text fontWeight="700">Itinerary</Text><Text mt={1}>{selectedPlan.itinerary.summary}</Text><Text mt={2} color={muted}>{selectedPlan.itinerary.days?.length || 0} day plan</Text></Box></>}
    </VStack>}</DrawerBody></DrawerContent></Drawer>
  </Box>;
}
